import { useQuery } from '@tanstack/react-query'
import moment from 'moment/moment'
import { ComponentProps, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { twMerge as twm } from 'tailwind-merge'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { PAYEE_TYPE } from '../../models/Payee'
import { RecordDataMap } from '../../models/RecordDataMap'
import { RecordFactory } from '../../models/RecordFactory'
import { Subaccount } from '../../models/Subaccount'
import { User } from '../../models/User'
import { apiClient } from '../../modules/apiclient'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { Icon } from '../Icon'
import Button from '../app/design/Button'
import { AddressPicker } from '../common/AddressPicker'
import { ClientSearchableSelect } from './ClientSearchableSelect'
import { DatePicker } from '../common/fields/DatePicker'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { TextArea } from '../common/fields/TextArea'
import { TextField } from '../common/fields/TextField'
import { CustomFieldRecordModal } from '../items/CustomFieldRecordModal'
import { WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    defaultSubaccount: Subaccount
    onClose: ComponentProps<typeof WorkflowPage>['onClose']
    owner: User
    onComplete: (subaccount: Subaccount) => void
    canEditClient?: boolean
}

export const NewSubaccountPage = ({
    defaultSubaccount,
    onClose,
    owner,
    onComplete,
    canEditClient = true,
}: Props) => {
    const navigate = useNavigate()
    const { logAndDisplayError } = useErrorHandler()
    const { findLastHistoryEntry } = useHistoryTrackerContext()

    const { data: payees } = useQuery({
        queryKey: ['payees', PAYEE_TYPE.PAYEE_TYPE_STAFF_ONLY],
        queryFn: () => RecordFactory.getInstance().loadPayees(PAYEE_TYPE.PAYEE_TYPE_STAFF_ONLY),
    })
    const { data: fields } = useQuery({
        queryKey: ['fields', owner.id],
        queryFn: () => RecordFactory.getInstance().loadSubaccountFields(owner.id ?? '', false),
    })
    console.log('fields', fields)

    const [subaccount, setSubaccount] = useState(defaultSubaccount)
    const [photo, setPhoto] = useState<
        { type: 'file'; file: File } | { type: 'url'; url: string } | null
    >(() => {
        const imageURL = subaccount.image?.url
        if (imageURL === undefined) {
            return null
        }
        return { type: 'url', url: imageURL }
    })

    const [isCustomFieldsModalOpen, setIsCustomFieldsModalOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    const canSave = !!subaccount.name && !!subaccount.client

    const handleSelectClient = (client: User | undefined) => {
        setSubaccount(subaccount.clone({ client }))
    }

    const handleSelectAddress = async (address: string) => {
        setSubaccount(subaccount.clone({ line1: address }))
        const result = await apiClient.post('/location/parse', { q: address })
        const data = result.data
        if (!data) {
            return
        }
        setSubaccount(
            subaccount.clone({
                line1: data.line1,
                line2: data.line2,
                city: data.city,
                state: data.state,
                postal_code: data.postal_code,
                country_code: data.country_code,
            })
        )
    }

    const payeeSearchableItems = [
        {
            id: '__none__',
            title: 'None',
            tertiaryView: <Icon icon="user.small" background="round" className="size-9 shrink-0" />,
            payee: undefined,
        },
        ...(payees ?? []).map((payee) => ({
            id: payee.id ?? '',
            title: payee.name ?? '',
            tertiaryView:
                payee.image?.uri !== undefined ? (
                    <img
                        src={payee.image.uri}
                        className="size-9 rounded-full border-2 border-white"
                    />
                ) : (
                    <Icon icon="user.small" background="round" className="size-9 shrink-0" />
                ),
            payee,
        })),
    ]
    const selectedPayeeItem = useMemo(() => {
        const payee = payees?.find((payee) => payee.id === subaccount.employee?.id)
        if (!payee) {
            return null
        }

        return {
            id: payee?.id ?? '',
            title: payee?.name ?? '',
            tertiaryView:
                payee?.image?.uri !== undefined ? (
                    <img
                        src={payee.image.uri}
                        className="size-6 rounded-full border-2 border-white"
                    />
                ) : (
                    <Icon icon="user.small" background="round" className="size-6 shrink-0" />
                ),
            payee,
        }
    }, [subaccount.employee])
    const handleSelectPayeeWithID = (payeeID: string | null) => {
        const payee = payees?.find((payee) => payee.id === payeeID)
        setSubaccount(subaccount.clone({ employee: payee }))
    }

    const onSave = async () => {
        if (!canSave) {
            return
        }

        setIsLoading(true)
        const result = await RecordFactory.getInstance().sendSubaccount(
            subaccount,
            photo?.type === 'file' ? photo.file : undefined
        )
        setIsLoading(false)

        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        onComplete(result.value)
    }

    const lastHistoryEntry = findLastHistoryEntry({ excluding: /^\/subaccounts.*/ })
    const onDelete = async () => {
        if (subaccount.id === undefined) {
            return
        }

        const result = await RecordFactory.getInstance().deleteSubaccount(subaccount.id)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        if (lastHistoryEntry) {
            lastHistoryEntry.goBackTo()
        }
        else {
            navigate('/clients')
        }
    }

    return (
        <WorkflowPage
            title={subaccount.labelString}
            onBack={() => navigate(-1)}
            onClose={onClose}
            footerContent={
                <div
                    className={twm(
                        'flex w-full items-center gap-x-4',
                        subaccount.id === undefined ? 'justify-end' : 'justify-between'
                    )}
                >
                    {subaccount.id !== undefined && (
                        <Button intent="secondary" className="text-danger-400" onClick={onDelete}>
                            Delete {subaccount.labelString}
                        </Button>
                    )}

                    <Button
                        intent="primary"
                        onClick={onSave}
                        isLoading={isLoading}
                        disabled={!canSave}
                    >
                        Save
                    </Button>
                </div>
            }
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start gap-y-6">
                <WorkflowPage.Card title="General">
                    <WorkflowPage.PhotoPicker
                        onSelectPhoto={(photoFile) => setPhoto({ type: 'file', file: photoFile })}
                        selectedPhoto={photo}
                        onClearPhoto={() => setPhoto(null)}
                    />

                    <WorkflowPage.IconSection
                        title="Name (required)"
                        icon={`${Subaccount.iconNameForSubaccountType(subaccount.type)}.large`}
                    >
                        <TextField
                            value={subaccount.name ?? ''}
                            onChange={(event) =>
                                setSubaccount(subaccount.clone({ name: event.target.value }))
                            }
                            onClear={() => setSubaccount(subaccount.clone({ name: '' }))}
                            placeholder="Enter your name"
                            size="medium"
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Client (required)"
                        icon="user.large"
                        disabled={subaccount.id !== undefined || !canEditClient}
                    >
                        <ClientSearchableSelect
                            mode="server"
                            selectedClient={subaccount.client}
                            onSelectClient={handleSelectClient}
                            shouldPreserveLastSelection
                            disabled={subaccount.id !== undefined || !canEditClient}
                        />
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>

                <WorkflowPage.Card title="Address">
                    <WorkflowPage.IconSection title="Street" icon="globeClock.large">
                        <AddressPicker
                            placeholder="Enter street"
                            onSelect={handleSelectAddress}
                            searchValue={subaccount.line1 ?? ''}
                            setSearchValue={(value) =>
                                setSubaccount(subaccount.clone({ line1: value }))
                            }
                            size="medium"
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Unit" icon="hash.large">
                        <TextField
                            value={subaccount.line2 ?? ''}
                            onChange={(event) =>
                                setSubaccount(subaccount.clone({ line2: event.target.value }))
                            }
                            onClear={() => setSubaccount(subaccount.clone({ line2: '' }))}
                            placeholder="Enter unit"
                            size="medium"
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="City" icon="markerPin.large">
                        <TextField
                            value={subaccount.city ?? ''}
                            onChange={(event) =>
                                setSubaccount(subaccount.clone({ city: event.target.value }))
                            }
                            onClear={() => setSubaccount(subaccount.clone({ city: '' }))}
                            placeholder="Enter city"
                            size="medium"
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Zipcode" icon="globeClock.large">
                        <TextField
                            value={subaccount.postalCode ?? ''}
                            onChange={(event) =>
                                setSubaccount(subaccount.clone({ postal_code: event.target.value }))
                            }
                            onClear={() => setSubaccount(subaccount.clone({ postal_code: '' }))}
                            placeholder="Enter zipcode"
                            size="medium"
                        />
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>

                <WorkflowPage.Card title="More details">
                    <WorkflowPage.IconSection
                        title="More details"
                        icon="form2.large"
                        description="Add more details"
                        hasHoverEffect
                        secondaryView={
                            <Button
                                onClick={() => setIsCustomFieldsModalOpen(true)}
                                intent="tertiaryLink"
                                className="hover:text-primary-700/60 mr-2 translate-x-3"
                                size="small"
                            >
                                Add
                            </Button>
                        }
                    />

                    <WorkflowPage.IconSection
                        title="Pro"
                        icon="employeeCard.large"
                        description="Staff somebody with this client"
                    >
                        <SearchableSelect
                            items={payeeSearchableItems}
                            onSelectItem={(item) =>
                                handleSelectPayeeWithID(item?.payee?.id ?? null)
                            }
                            selectedItemOverride={selectedPayeeItem}
                            placeholder="Type to search"
                            size="medium"
                            shouldPreserveLastSelection
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Birthday" icon="calendar.large">
                        <DatePicker
                            size="medium"
                            selectedDateString={
                                subaccount.birthDay?.format('MM/DD/YYYY') ??
                                moment().format('MM/DD/YYYY')
                            }
                            onSelectDate={(newDate) => {
                                setSubaccount(subaccount.clone({ birthday: moment(newDate) }))
                            }}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Description"
                        icon="note.large"
                        secondaryText={`${(subaccount.memo ?? '').length} / 1200`}
                    >
                        <TextArea
                            size="medium"
                            isAutoResizing
                            placeholder="Add a description"
                            className="overlow-y-hidden"
                            value={subaccount.memo ?? ''}
                            onChange={(event) =>
                                setSubaccount(subaccount.clone({ memo: event.target.value }))
                            }
                            maxLength={1200}
                            onClear={() => setSubaccount(subaccount.clone({ memo: '' }))}
                        />
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>
            </div>

            <CustomFieldRecordModal
                isOpen={isCustomFieldsModalOpen}
                onClose={() => setIsCustomFieldsModalOpen(false)}
                title="More details"
                fields={fields ?? []}
                answers={subaccount.fieldValues ?? {}}
                onUpdateAnswers={(newFieldValues) =>
                    setSubaccount(
                        subaccount.clone({ field_values: new RecordDataMap(newFieldValues) })
                    )
                }
                shouldEnforceRequired={false}
            />
        </WorkflowPage>
    )
}
